import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { TaxScenarioInput, TaxResults } from '@/tax/types';
import { DEFAULT_INPUT } from '@/tax/types';
import { calculateTaxServer } from '@/tax/taxApi';
import { reportCalcFailure } from '@/lib/calcFailure';
import { WIZARD_STEPS } from './wizard/types';
import WizardStep from './wizard/WizardStep';
import { GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import FilingStep from './wizard/steps/FilingStep';
import IncomeStep from './wizard/steps/IncomeStep';
import BusinessStep from './wizard/steps/BusinessStep';
import RentalK1Step from './wizard/steps/RentalK1Step';
import DeductionsStep from './wizard/steps/DeductionsStep';
import CreditsStep from './wizard/steps/CreditsStep';
import StrategyBuilderStep, { type StrategyVariant, makeDefaultVariant } from './wizard/steps/StrategyBuilderStep';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

/** Legacy localStorage draft helpers — kept for migration/cleanup only */
const DRAFT_KEY = 'tax-wizard-draft';
export function loadDraft() { return null; }
export function clearDraft() { localStorage.removeItem(DRAFT_KEY); }

export interface WizardSubmitPayload {
  baseInput: TaxScenarioInput;
  variants: StrategyVariant[];
  /** 'compare' = jump to comparison view post-save (only meaningful when editing
   * a baseline that has strategies). Default behavior depends on the parent. */
  intent?: 'default' | 'compare';
}

interface ScenarioWizardProps {
  initial?: TaxScenarioInput;
  /** Initial variants to restore (used when editing an existing scenario set) */
  initialVariants?: StrategyVariant[];
  onSubmit: (payload: WizardSubmitPayload) => void;
  onAutoSaveDraft?: (input: TaxScenarioInput, step: number) => void;
  submitLabel?: string;
  currentStep: number;
  onStepChange: (step: number) => void;
  sourceScenarioName?: string;
  visitedSteps: Set<number>;
  isNewScenario?: boolean;
  /** Ref that the sidebar "Save & Calculate" button can call from outside */
  submitRef?: React.MutableRefObject<(() => void) | null>;
  /** Ref that the sidebar "Save & Compare" button can call from outside */
  submitCompareRef?: React.MutableRefObject<(() => void) | null>;
  /** When true, the Strategies step renders variant cards as read-only (baseline-edit mode). */
  strategiesReadOnly?: boolean;
}

const STRATEGY_STEP = WIZARD_STEPS.length - 1; // last step index

export default function ScenarioWizard({
  initial,
  initialVariants,
  onSubmit,
  onAutoSaveDraft,
  currentStep,
  onStepChange,
  sourceScenarioName,
  visitedSteps,
  isNewScenario,
  submitRef,
  submitCompareRef,
  strategiesReadOnly = false,
}: ScenarioWizardProps) {
  const [input, setInput] = useState<TaxScenarioInput>(initial || { ...DEFAULT_INPUT });
  const [variants, setVariants] = useState<StrategyVariant[]>(initialVariants || []);
  const [baselineResults, setBaselineResults] = useState<TaxResults | null>(null);
  const [baselineCalculating, setBaselineCalculating] = useState(false);
  const isInitialRender = useRef(true);

  // 1040 PDF import is gated behind the org-level "import_1040" feature flag.
  // Super admins manage rollout from SuperAdmin → Feature Flags. Until enabled
  // for the user's org (or globally), the import button does not render.
  const { isEnabled } = useFeatureFlags();
  const import1040Enabled = isEnabled('import_1040');
  // The import screen's W-2 wage split box, off until Dylan has looked at it.
  const wageSplitBox = isEnabled('import_wage_split');
  // Birth-year boxes on Filing & Personal, for the multi-year Roth plan. Off for everyone until pinned.
  const birthYears = isEnabled('roth_plan');

  const onChange = useCallback(<K extends keyof TaxScenarioInput>(key: K, value: TaxScenarioInput[K]) => {
    setInput(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Bulk-apply parsed 1040 fields. Only overwrites the keys present in `fields`. */
  const onImport1040 = useCallback((fields: Partial<TaxScenarioInput>) => {
    setInput(prev => ({ ...prev, ...fields }));
  }, []);

  // Calculate baseline whenever we're on the strategies step and base input changes
  useEffect(() => {
    if (currentStep !== STRATEGY_STEP) return;
    let cancelled = false;
    setBaselineCalculating(true);
    const t = setTimeout(async () => {
      const baselineInput: TaxScenarioInput = {
        ...input,
        alternativeInvestmentAmount: 0,
      };
      try {
        const result = await calculateTaxServer(baselineInput);
        if (!cancelled) { setBaselineResults(result); setBaselineCalculating(false); }
      } catch (err) {
        if (!cancelled) {
          setBaselineCalculating(false);
          reportCalcFailure(err, 'baseline');
        }
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, input]);

  // Auto-save draft whenever the user navigates to a different step
  useEffect(() => {
    if (isInitialRender.current) { isInitialRender.current = false; return; }
    if (onAutoSaveDraft) onAutoSaveDraft(input, currentStep);
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitWithIntent = (intent: 'default' | 'compare') => {
    if (input.qualifiedDividends > input.ordinaryDividends) {
      toast.error('Qualified Dividends cannot exceed Total Dividends. Fix the Income step before saving.');
      onStepChange(1);
      return;
    }
    onSubmit({ baseInput: input, variants, intent });
  };
  const handleSubmit = () => submitWithIntent('default');
  const handleSubmitAndCompare = () => submitWithIntent('compare');

  // Keep the sidebar submit refs pointing at the latest handlers
  if (submitRef) submitRef.current = handleSubmit;
  if (submitCompareRef) submitCompareRef.current = handleSubmitAndCompare;

  const goBack = () => onStepChange(Math.max(0, currentStep - 1));
  const goNext = () => onStepChange(Math.min(WIZARD_STEPS.length - 1, currentStep + 1));

  const safeStep = Math.max(0, Math.min(currentStep, WIZARD_STEPS.length - 1));
  const stepConfig = WIZARD_STEPS[safeStep];
  const isLast = currentStep === WIZARD_STEPS.length - 1;

  const dividendsInvalid = input.qualifiedDividends > input.ordinaryDividends;
  const nextDisabled = dividendsInvalid;
  const nextDisabledReason = dividendsInvalid
    ? 'Qualified Dividends cannot exceed Total Dividends. Fix the Income step before continuing.'
    : undefined;

  const stepContent = (() => {
    switch (currentStep) {
      case 0: return <FilingStep input={input} onChange={onChange} onImport1040={isNewScenario && import1040Enabled ? onImport1040 : undefined} wageSplitBox={wageSplitBox} birthYears={birthYears} />;
      case 1: return <IncomeStep input={input} onChange={onChange} filingStatus={input.filingStatus} />;
      case 2: return <BusinessStep input={input} onChange={onChange} filingStatus={input.filingStatus} />;
      case 3: return <RentalK1Step input={input} onChange={onChange} />;
      case 4: return <DeductionsStep input={input} onChange={onChange} />;
      case 5: return <CreditsStep input={input} onChange={onChange} />;
      case 6: return (
        <StrategyBuilderStep
          input={input}
          variants={variants}
          onVariantsChange={setVariants}
          baselineResults={baselineResults}
          baselineCalculating={baselineCalculating}
          readOnly={strategiesReadOnly}
        />
      );
      default: return null;
    }
  })();

  return (
    <div className="space-y-6">
      {sourceScenarioName && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
          <GitBranch className="h-4 w-4 shrink-0" />
          <span>Comparing against <strong>{sourceScenarioName}</strong> — adjust values, then calculate to compare side-by-side.</span>
        </div>
      )}
      <WizardStep
        title={stepConfig.label}
        stepNumber={currentStep + 1}
        totalSteps={WIZARD_STEPS.length}
        onBack={currentStep > 0 ? goBack : undefined}
        onNext={!isLast ? goNext : undefined}
        onSubmit={isLast ? handleSubmit : undefined}
        onSubmitAndCompare={
          isLast && strategiesReadOnly && variants.length > 0
            ? handleSubmitAndCompare
            : undefined
        }
        isLast={isLast}
        nextDisabled={nextDisabled}
        nextDisabledReason={nextDisabledReason}
      >
        {stepContent}
      </WizardStep>
    </div>
  );
}
